import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import pages from "../pages";
import logo from "../image/SVG/logo.svg";

interface ReadProps {
    selectTabHome?: () => void;
}

const Read = (props: ReadProps) => {
    //Displays images and pieces of text stored in an array
    //Each page is represented by a .jsx element directly exported from Adobe InDesign

    const { pageNumber: urlPageNumber } = useParams<{ pageNumber: string }>();
    const navigate = useNavigate();
    const [pageNumber, setPageNumber] = useState(0);
    const [windowDimensions, setWindowDimensions] = useState(
        getWindowDimensions()
    );
    const [arrowsHidden, setArrowsHidden] = useState(false);
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
    const [canScroll, setCanScroll] = useState(true);

    // Handle URL parameter changes
    useEffect(() => {
        if (urlPageNumber) {
            const pageNum = parseInt(urlPageNumber, 10);
            if (!isNaN(pageNum)) {
                // Clamp to valid range
                const clampedPageNum = Math.max(
                    0,
                    Math.min(pageNum, pages.length - 1)
                );
                // Round to even number
                const evenPageNum =
                    clampedPageNum % 2 === 0
                        ? clampedPageNum
                        : clampedPageNum - 1;
                setPageNumber(evenPageNum);

                // If the original page number was out of bounds, redirect to the valid page
                if (pageNum !== clampedPageNum) {
                    navigate(`/page/${evenPageNum}`, { replace: true });
                }
            } else {
                // Invalid page number, redirect to page 0
                navigate("/page/0", { replace: true });
            }
        }
    }, [urlPageNumber, navigate]);

    const formatPoemList = () => {
        let poemTitleArray = [];

        for (let i = 0; i < pages.length; i++) {
            if (pages[i].isImage === false && pages[i].title) {
                poemTitleArray.push({
                    title: pages[i].title,
                    pageIndex: i,
                });
            }
        }
        poemTitleArray = poemTitleArray.map((elem) => {
            // Round down to even number for navigation
            const evenPageIndex =
                elem.pageIndex % 2 === 0 ? elem.pageIndex : elem.pageIndex - 1;
            return (
                <li
                    key={`poem-${elem.title}`}
                    onClick={() => {
                        setPageNumber(evenPageIndex);
                        navigate(`/page/${evenPageIndex}`);
                    }}
                    className={pageNumber === evenPageIndex ? "selected" : ""}
                >
                    {elem.title}
                </li>
            );
        });

        return <ul>{poemTitleArray}</ul>;
    };

    const goToNextPages = useCallback(() => {
        const maxPage = pages.length - 2; // Ensure we don't go beyond the last page
        if (pageNumber < maxPage) {
            const newPageNumber = Math.min(pageNumber + 2, maxPage);
            setPageNumber(newPageNumber);
            navigate(`/page/${newPageNumber}`);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    }, [pageNumber, navigate]);

    const goToPreviousPages = useCallback(() => {
        if (pageNumber > 0) {
            const newPageNumber = Math.max(pageNumber - 2, 0);
            setPageNumber(newPageNumber);
            navigate(`/page/${newPageNumber}`);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    }, [pageNumber, navigate]);

    // Ensure pageNumber is within bounds
    const safePageNumber = Math.max(0, Math.min(pageNumber, pages.length - 1));

    let pageA: React.ReactNode | undefined;
    let pageATitle: string | undefined;
    if (pages[safePageNumber] && pages[safePageNumber].title) {
        pageATitle = pages[safePageNumber].title;
    }
    if (pages[safePageNumber] && pages[safePageNumber].isImage) {
        pageA = pages[safePageNumber].image;
    } else if (pages[safePageNumber]) {
        pageA = pages[safePageNumber].text;
    }

    let pageB: React.ReactNode | undefined;
    let pageBTitle: string | undefined;
    const nextPageIndex = safePageNumber + 1;
    if (pages[nextPageIndex] && pages[nextPageIndex].title) {
        pageBTitle = pages[nextPageIndex].title;
    }
    if (pages[nextPageIndex] && pages[nextPageIndex].isImage) {
        pageB = pages[nextPageIndex].image;
    } else if (pages[nextPageIndex]) {
        pageB = pages[nextPageIndex].text;
    }

    function getWindowDimensions() {
        const { innerWidth: width, innerHeight: height } = window;
        return {
            width,
            height,
        };
    }

    const handleScroll = () => {
        setArrowsHidden(false);
        if (timer !== null) {
            clearTimeout(timer);
        }
        setTimer(setTimeout(() => setArrowsHidden(true), 3000));
    };

    function scrollbarVisible(element: HTMLElement) {
        return element.scrollHeight > element.clientHeight;
    }

    useEffect(() => {
        const root = document.getElementById("root");
        if (root) {
            setCanScroll(scrollbarVisible(root));
        }
    }, []);

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());

            const root = document.getElementById("root");
            if (root) {
                setCanScroll(scrollbarVisible(root));
            }
        }

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                goToPreviousPages();
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                goToNextPages();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [goToPreviousPages, goToNextPages]);

    return (
        <div className="readTab">
            <nav>
                <div className="poemsforandroids-title">
                    <img src={logo} alt="Poems for Androids" />
                </div>
                <button onClick={() => navigate("/")}>home</button>
            </nav>

            <div className="readMain">
                <div className="poemList">{formatPoemList()}</div>

                <button
                    className={`pagePrevious${
                        arrowsHidden && canScroll ? " arrowsHidden" : ""
                    }`}
                    disabled={safePageNumber <= 0}
                    onClick={goToPreviousPages}
                >
                    {"<"}
                </button>
                <div className="book">
                    <div className="page">
                        {pageATitle && <h1>{pageATitle}</h1>}
                        {pageA}
                    </div>

                    <div className="page">
                        {pageBTitle && <h1>{pageBTitle}</h1>}
                        {pageB}
                    </div>
                    {pageNumber === 0 ? (
                        <div className="arrowsinfo">
                            <p>
                                Use the arrows on the sides to go to the
                                next/previous page.
                            </p>
                        </div>
                    ) : null}
                </div>
                <button
                    className={`pageNext${
                        arrowsHidden && canScroll ? " arrowsHidden" : ""
                    }`}
                    disabled={safePageNumber >= pages.length - 2}
                    onClick={goToNextPages}
                >
                    {">"}
                </button>
            </div>
        </div>
    );
};

export default Read;
